// src/components/instructor/ops/TeamAccessControls.js
//
// Pass 58A — the Team control-centre ACCESS/SECURITY controls: invite, resend, change role, and
// disable/enable (revoke) app access. OWNER/ADMIN ONLY — OPS must NOT administer access/security, so
// the whole panel is hidden unless canManageOrganization. It only WIRES the existing safe writers in
// teamAccessWriters.js (no new access logic), passes the actor context (role auth + audit stamp), and
// NEVER fabricates an email: a member with no stored address is EMAIL REQUIRED and cannot be invited
// until an operator supplies a real one. No email is dispatched in this pass (writers are Firestore-only
// and remain fail-closed in production).

import React, { useState } from 'react';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { canManageOrganization, canAdministerOwner } from '../../../utils/platformAccess';
import { PLATFORM_ROLES } from '../../../utils/platformSchema';
import {
  teamAccessUiState, hasApprovedEmail, permissionRoleOf, memberDocId, TEAM_UI_STATE, TEAM_UI_STATE_LABEL, PERMISSION_LABEL,
} from '../../../utils/teamAccess';
import { changePermissionRole, disableAccess, enableAccess } from '../../../utils/teamAccessWriters';
// Pass 58B — ONE canonical invite model. The Team UI creates/resends invites through
// organizationInvites.js (inviteCode + emailNormalized + role + targetMemberUserId), the SAME schema the
// /invite/:inviteCode acceptance route consumes. The legacy invitePersonToMotus/resendInvite path is not
// used here anymore.
import { createOrganizationInvite, resendOrganizationInvite } from '../../../utils/organizationInvites';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Pass 88.4 — the build-time provider-ready gate (REACT_APP_INVITE_EMAIL_PROVIDER_READY) is REMOVED from the
// production send decision. That brittle gate was the proven cause of a live outage: a frontend built WITHOUT
// the CI env (e.g. a local `firebase deploy --only hosting`) inlined the flag to `false`, so the deployed UI
// silently refused to send and only offered "Copy invite link" — even though the server sender was deployed
// and working. Email delivery is now a permanent SERVER capability (voletus.com verified, RESEND_API_KEY set,
// sendInviteEmail deployed + fail-closed). The browser therefore ATTEMPTS the explicit, operator-triggered send
// and shows the server's TRUTHFUL result (sent / created-but-could-not-send), rather than trusting a stale
// build-time boolean. Still an explicit per-member click — NO auto-send, NO bulk/"send all" action.

// Pass 58D — the ONLY invite send path: canonical invite → server
// sendInviteEmail (Resend) → delivery result. We show "Invitation sent" ONLY after the provider delivers;
// if the invite was created but the email failed we say so and offer Retry email + Copy invite link (we
// never create a second invite).
function callSendInviteEmail(inviteCode) {
  const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendInviteEmail');
  return fn({ inviteCode });
}
const inviteUrl = (code) => `${(typeof window !== 'undefined' && window.location ? window.location.origin : '')}/invite/${code}`;

// The ONLY selectable organization roles. Platform roles (super/content admin) and the legacy
// 'organization' alias are deliberately NOT offered.
const ROLE_OPTIONS = [
  [PLATFORM_ROLES.ORGANIZATION_OWNER, PERMISSION_LABEL.organization_owner],
  [PLATFORM_ROLES.ORGANIZATION_ADMIN, PERMISSION_LABEL.organization_admin],
  [PLATFORM_ROLES.ORGANIZATION_OPS, PERMISSION_LABEL.ops],
  [PLATFORM_ROLES.INSTRUCTOR, PERMISSION_LABEL.instructor],
];

const STATE_COLOR = {
  email_required: { bg: '#FEF3C7', fg: '#92400E' },
  ready_to_invite: { bg: '#EFF4FE', fg: '#1A3FA0' },
  invite_pending: { bg: '#F5F3FF', fg: '#4C1D95' },
  active: { bg: '#E1F5EE', fg: '#085041' },
  disabled: { bg: '#FEE2E2', fg: '#991B1B' },
};

export default function TeamAccessControls({ member, organizationId }) {
  const { currentUser, userData } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [lastInviteCode, setLastInviteCode] = useState('');
  const [emailState, setEmailState] = useState('idle'); // idle | sent | failed

  // OWNER/ADMIN ONLY. Ops (and everyone else) never sees access/security administration.
  const canManage = canManageOrganization(userData, organizationId);
  if (!member || !canManage) return null;

  const state = teamAccessUiState(member);
  const roleNow = permissionRoleOf(member);
  const actor = { userData, organizationId, actorUserId: currentUser?.uid || '', actorName: userData?.name || '' };
  const storedEmail = member.email || member.contactEmail || '';
  const c = STATE_COLOR[state] || STATE_COLOR.email_required;
  // Owner-role security: only an owner/super may create/promote/demote/disable the owner. A plain admin
  // sees neither the Owner option nor any control over an existing owner (also enforced in the writer + rules).
  const actorCanOwner = canAdministerOwner(userData, organizationId);
  const targetIsOwner = roleNow === PLATFORM_ROLES.ORGANIZATION_OWNER;
  const ownerLocked = targetIsOwner && !actorCanOwner;
  const roleOptions = ROLE_OPTIONS.filter(([v]) => actorCanOwner || v !== PLATFORM_ROLES.ORGANIZATION_OWNER);
  const inviteCommon = { organizationId, organizationName: member.organizationName || '', role: roleNow, invitedByUserId: actor.actorUserId, invitedByName: actor.actorName, targetMemberId: memberDocId(member), targetMemberUserId: member.userId };

  const run = async (fn, okMsg) => {
    setMsg(''); setBusy(true);
    try { await fn(); setMsg(okMsg); }
    catch (e) { setMsg(e?.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  // Pass 86P — dev-safe timing instrumentation (never in production, no PII). Measures the callable round-trip
  // and the total click-to-result so the ~5 s send latency is observable without a third-party vendor.
  const mark = (label, ms) => { if (process.env.NODE_ENV !== 'production') { try { console.debug(`[invite-timing] ${label}: ${Math.round(ms)}ms`); } catch (_) { /* ignore */ } } };

  // Send (or resend) the canonical invite's email; set the exact state — never a false "sent". The success
  // message names the canonical recipient so the operator sees exactly where it went.
  const sendEmailFor = async (code, recipient = '') => {
    setLastInviteCode(code);
    const t = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      await callSendInviteEmail(code);
      mark('callable round-trip', (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t);
      setEmailState('sent'); setMsg(recipient ? `Invitation sent to ${recipient}.` : 'Invitation sent.');
    } catch (e) {
      mark('callable round-trip (failed)', (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t);
      // TRUTHFUL failure — the canonical invite still exists; offer Retry + Copy link (never a second invite).
      setEmailState('failed'); setMsg('Invitation created — email could not be sent.');
    }
  };
  // Invite: create the canonical invite ONCE, then ATTEMPT the explicit send and show the server's truthful
  // result. No build-time gate. The caller button is `disabled={busy}`, so a double-click cannot fire a
  // second create/send.
  const doInvite = async (email) => {
    if (busy) return; // extra guard against a double-submit race before the disabled state paints
    setMsg(''); setBusy(true); setEmailState('sending');
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      const tc = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const inv = await createOrganizationInvite(db, { ...inviteCommon, email });
      mark('invite create', (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tc);
      const code = inv.inviteCode || inv.id;
      await sendEmailFor(code, inv.email || email);
      mark('total click→result', (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
    } catch (e) { setEmailState('idle'); setMsg(e?.message || 'Invite failed'); }
    finally { setBusy(false); }
  };
  const doResend = async () => {
    if (busy) return; // double-submit guard (button is also disabled while busy)
    setMsg(''); setBusy(true); setEmailState('sending');
    try {
      const r = await resendOrganizationInvite(db, { organizationId, email: storedEmail, actorUserId: actor.actorUserId });
      await sendEmailFor(r.inviteCode, storedEmail);
    } catch (e) { setEmailState('idle'); setMsg(e?.message || 'Resend failed'); }
    finally { setBusy(false); }
  };
  // Pass 86P — explicit async button label: on click the button immediately disables and shows an in-progress
  // label (never a silent multi-second wait). Success/failure then render the durable 'sent'/'failed' states below.
  const sendBtnLabel = (idleLabel) => (busy ? 'Sending…' : idleLabel);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteUrl(lastInviteCode)); setMsg('Invite link copied.'); }
    catch { setMsg(inviteUrl(lastInviteCode)); }
  };

  const emailForInvite = hasApprovedEmail(member) ? storedEmail : emailInput.trim();
  const canInvite = /.+@.+\..+/.test(emailForInvite);

  return (
    <div data-testid="team-access-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 12, marginTop: 8, paddingTop: 8, borderTop: '1px dashed #ECEAE6' }}>
      <span data-testid="access-state" style={{ padding: '2px 8px', borderRadius: 999, background: c.bg, color: c.fg, fontWeight: 700 }}>
        {TEAM_UI_STATE_LABEL[state]}
      </span>

      {/* Role editor — org roles only; preserves identity + auth link (writer sets orgRole only).
          Owner transitions are hidden/locked for non-owner actors. */}
      <span>
        Role:{' '}
        <select data-testid="role-select" value={roleNow} disabled={busy || ownerLocked}
          onChange={(e) => run(() => changePermissionRole(db, member, e.target.value, actor), 'Role updated')}>
          {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          {/* If the member currently holds a role not in the offered set (e.g. an existing owner viewed by
              an admin, or a legacy role), show it as the selected value without offering it as a choice. */}
          {!roleOptions.some(([v]) => v === roleNow) && <option value={roleNow}>{PERMISSION_LABEL[roleNow] || roleNow}</option>}
        </select>
      </span>

      {/* Invite / Email-required */}
      {state === TEAM_UI_STATE.EMAIL_REQUIRED && (
        <span data-testid="email-required" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#92400E' }}>
          Email required
          <input data-testid="invite-email" type="email" placeholder="add a real email" value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)} style={{ fontSize: 12, padding: 3, width: 170 }} />
          <button type="button" data-testid="invite-btn" disabled={busy || !canInvite}
            onClick={() => doInvite(emailForInvite)}
            style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #ccc', background: canInvite ? '#fff' : '#F3F4F6', cursor: canInvite ? 'pointer' : 'not-allowed' }}>{sendBtnLabel('Send invitation')}</button>
        </span>
      )}
      {state === TEAM_UI_STATE.READY_TO_INVITE && (
        <button type="button" data-testid="invite-btn" disabled={busy}
          onClick={() => doInvite(storedEmail)}
          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #1A3FA0', color: '#1A3FA0', background: '#fff', cursor: busy ? 'wait' : 'pointer' }}>{sendBtnLabel(`Send invitation to ${storedEmail}`)}</button>
      )}
      {state === TEAM_UI_STATE.INVITE_PENDING && (
        <button type="button" data-testid="resend-btn" disabled={busy}
          onClick={doResend}
          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #4C1D95', color: '#4C1D95', background: '#fff', cursor: busy ? 'wait' : 'pointer' }}>{sendBtnLabel('Resend invitation')}</button>
      )}

      {/* Email failed → the invite still exists; offer Retry + Copy link (never create a second invite). */}
      {emailState === 'failed' && lastInviteCode && (
        <span data-testid="email-failed" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <button type="button" data-testid="retry-email-btn" disabled={busy} onClick={() => sendEmailFor(lastInviteCode, storedEmail)}
            style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #92400E', color: '#92400E', background: '#fff', cursor: 'pointer' }}>Retry email</button>
          <button type="button" data-testid="copy-link-btn" onClick={copyLink}
            style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Copy invite link</button>
        </span>
      )}
      {emailState === 'sent' && lastInviteCode && (
        <button type="button" data-testid="copy-link-btn" onClick={copyLink}
          style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Copy invite link</button>
      )}

      {/* Revoke / restore access — locked for an owner target unless the actor may administer the owner. */}
      {ownerLocked ? (
        <span data-testid="owner-locked" style={{ color: '#9E9B95' }}>Owner — managed by owner/super-admin only</span>
      ) : state === TEAM_UI_STATE.DISABLED ? (
        <button type="button" data-testid="enable-btn" disabled={busy}
          onClick={() => run(() => enableAccess(db, member, actor), 'Access restored')}
          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #085041', color: '#085041', background: '#fff', cursor: 'pointer' }}>Enable access</button>
      ) : (
        <button type="button" data-testid="revoke-btn" disabled={busy}
          onClick={() => run(() => disableAccess(db, member, actor), 'Access disabled')}
          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #DC2626', color: '#DC2626', background: '#fff', cursor: 'pointer' }}>Disable access</button>
      )}

      {msg && <span data-testid="tac-msg" style={{ color: '#6B7280' }}>{msg}</span>}
    </div>
  );
}
